import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"

import SelectDescription from "components/forms/select-description"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import usePurchaseReportRepositories from "repositories/purchase-report"
import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"

const formatNumber = (value) =>
  Number(value ?? 0).toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const formatQty = (value) =>
  Number(value ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })

const toInputDate = (value) => {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) return ""

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

const STATUS_OPTIONS = [
  { id: "POSTED", name: "Posted" },
  { id: "DRAFT", name: "Draft" },
  { id: "CANCELLED", name: "Cancelled" },
  { id: "ALL", name: "Semua Status" },
]

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()

  const startOfMonth = new Date()
  startOfMonth.setDate(1)

  const dateFrom = searchParams.get("date_from") || toInputDate(startOfMonth)
  const dateTo = searchParams.get("date_to") || toInputDate(new Date())
  const outletId = searchParams.get("outlet_id") || getDefaultOutletFilterValue()
  const status = searchParams.get("status") || "POSTED"
  const mode = searchParams.get("mode") || "global"

  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletId)

  const { data: reportData, isLoading } = usePurchaseReportRepositories({
    date_from: dateFrom,
    date_to: dateTo,
    outlet_id: resolvedOutletCsv || undefined,
    status,
    enabled: true,
  })

  const globalRows = useMemo(() => reportData?.global?.rows ?? [], [reportData])
  const globalSummary = useMemo(() => reportData?.global?.summary ?? {}, [reportData])
  const detailReceipts = useMemo(() => reportData?.detail?.receipts ?? [], [reportData])
  const detailSummary = useMemo(() => reportData?.detail?.summary ?? {}, [reportData])

  const mergeSearchParams = (nextValues) => {
    const nextParams = new URLSearchParams(searchParams)

    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })

    setSearchParams(nextParams)
  }

  const handleExport = () => {
    if (!dateFrom || !dateTo) {
      toast.error("Periode wajib diisi sebelum export")
      return
    }

    const query = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
      status,
      mode,
    })
    if (resolvedOutletCsv) query.set("outlet_id", resolvedOutletCsv)

    window.open(
      `${process.env.REACT_APP_INVENTORY_API_URL}/reports/purchase/export?${query.toString()}`,
      "_blank"
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 p-5 bg-white border border-neutral-200 rounded-2xl lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Laporan Pembelian</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Pembelian (barang masuk) per periode tanggal bukti, tampil global (ringkasan per faktur) maupun detail per item.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="min-w-[180px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Tipe Laporan</label>
            <SelectDescription
              selection={[
                { id: "global", name: "Global" },
                { id: "detail", name: "Detail" },
              ]}
              value={mode}
              onChange={(value) => mergeSearchParams({ mode: value || "global" })}
              keyValue={(v) => v.id}
              title={(v) => v.name}
              placeholder="Pilih Tipe"
            />
          </div>

          <div className="min-w-[200px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Outlet</label>
            <OutletFilterSelect
              value={outletId}
              onChange={(v) => mergeSearchParams({ outlet_id: v })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              title="Outlet"
            />
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Status</label>
            <SelectDescription
              selection={STATUS_OPTIONS}
              value={status}
              onChange={(value) => mergeSearchParams({ status: value || "POSTED" })}
              keyValue={(v) => v.id}
              title={(v) => v.name}
              placeholder="Pilih Status"
            />
          </div>

          <div className="min-w-[150px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Tanggal Dari</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={dateFrom}
              onChange={(event) => mergeSearchParams({ date_from: event.target.value })}
            />
          </div>

          <div className="min-w-[150px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Tanggal Sampai</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={dateTo}
              onChange={(event) => mergeSearchParams({ date_to: event.target.value })}
            />
          </div>

          <button
            type="button"
            onClick={handleExport}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800"
          >
            Ekspor Excel
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Jumlah Faktur</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatQty(globalSummary.total_faktur)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Qty</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatQty(globalSummary.qty)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Nilai</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(globalSummary.value)}</div>
        </div>
      </div>

      <div className="overflow-hidden bg-white border border-neutral-200 rounded-2xl">
        <div className="px-4 py-4 border-b border-neutral-200 bg-neutral-50">
          <div className="text-sm font-semibold text-neutral-900">{reportData?.branch_name || "SEMUA OUTLET"}</div>
          <div className="mt-1 text-xs text-neutral-500">{reportData?.period_label || "-"}</div>
        </div>

        {mode === "global" ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-100">
                <tr>
                  <th className="px-4 py-3 font-semibold text-left text-neutral-700">NO. FAKTUR</th>
                  <th className="px-4 py-3 font-semibold text-left text-neutral-700">TGL. FAKTUR</th>
                  <th className="px-4 py-3 font-semibold text-left text-neutral-700">NO. BUKTI</th>
                  <th className="px-4 py-3 font-semibold text-left text-neutral-700">TGL. BUKTI/JAM</th>
                  <th className="px-4 py-3 font-semibold text-left text-neutral-700">OUTLET</th>
                  <th className="px-4 py-3 font-semibold text-left text-neutral-700">USER</th>
                  <th className="px-4 py-3 font-semibold text-left text-neutral-700">SUPPLIER</th>
                  <th className="px-4 py-3 font-semibold text-left text-neutral-700">STATUS</th>
                  <th className="px-4 py-3 font-semibold text-right text-neutral-700">QTY</th>
                  <th className="px-4 py-3 font-semibold text-right text-neutral-700">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={10} className="px-4 py-10 text-center text-neutral-500">Memuat data laporan...</td>
                  </tr>
                )}

                {!isLoading && globalRows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-10 text-center text-neutral-500">
                      Tidak ada data pembelian untuk periode yang dipilih.
                    </td>
                  </tr>
                )}

                {!isLoading && globalRows.map((row) => (
                  <tr key={row.bukti_number} className="border-t border-neutral-200 hover:bg-brand-50 transition-colors">
                    <td className="px-4 py-3 font-medium whitespace-nowrap text-neutral-900">{row.faktur_number}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-neutral-800">{row.faktur_date_label}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-neutral-800">{row.bukti_number}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-neutral-800">{row.bukti_date_label} {row.bukti_time !== "-" ? row.bukti_time : ""}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-neutral-800">{row.outlet_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-neutral-800">{row.user}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-neutral-800">{row.supplier}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-neutral-800">{row.status}</td>
                    <td className="px-4 py-3 text-right text-neutral-800">{formatQty(row.qty)}</td>
                    <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.value)}</td>
                  </tr>
                ))}
              </tbody>
              {!isLoading && globalRows.length > 0 && (
                <tfoot className="bg-neutral-50">
                  <tr className="border-t border-neutral-200">
                    <td className="px-4 py-3 font-semibold text-neutral-900" colSpan={8}>TOTAL QTY</td>
                    <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatQty(globalSummary.qty)}</td>
                    <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(globalSummary.value)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        ) : (
          <div className="p-4 space-y-6">
            {isLoading && (
              <div className="py-10 text-center text-neutral-500">Memuat data laporan...</div>
            )}

            {!isLoading && detailReceipts.length === 0 && (
              <div className="py-10 text-center text-neutral-500">
                Tidak ada data pembelian untuk periode yang dipilih.
              </div>
            )}

            {!isLoading && detailReceipts.map((receipt) => (
              <div key={receipt.bukti_number} className="overflow-hidden border border-neutral-200 rounded-xl">
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-neutral-50">
                  <div className="text-sm font-semibold text-neutral-900">
                    NO. FAKTUR : {receipt.faktur_number}
                    <span className="ml-3 text-xs font-normal text-neutral-500">OUTLET: {receipt.outlet_name}</span>
                    <span className="ml-3 text-xs font-normal text-neutral-500">USER: {receipt.user}</span>
                    <span className="ml-3 text-xs font-normal text-neutral-500">STATUS: {receipt.status}</span>
                  </div>
                  <div className="text-xs text-neutral-600">
                    NO. BUKTI : {receipt.bukti_number} &middot; TGL FAKTUR : {receipt.faktur_date_label} &middot; TGL BUKTI : {receipt.bukti_date_label} {receipt.bukti_time !== "-" ? receipt.bukti_time : ""}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-neutral-100">
                      <tr>
                        <th className="px-4 py-2 font-semibold text-left text-neutral-700">KODE BARANG</th>
                        <th className="px-4 py-2 font-semibold text-right text-neutral-700">QTY</th>
                        <th className="px-4 py-2 font-semibold text-left text-neutral-700">SATUAN</th>
                        <th className="px-4 py-2 font-semibold text-right text-neutral-700">HARGA</th>
                        <th className="px-4 py-2 font-semibold text-right text-neutral-700">TOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receipt.items.map((item, index) => (
                        <tr key={`${receipt.bukti_number}-${index}`} className="border-t border-neutral-200">
                          <td className="px-4 py-2 text-neutral-800">{item.product_code}</td>
                          <td className="px-4 py-2 text-right text-neutral-800">{formatQty(item.qty)}</td>
                          <td className="px-4 py-2 text-neutral-800">{item.uom}</td>
                          <td className="px-4 py-2 text-right text-neutral-800">{formatNumber(item.price)}</td>
                          <td className="px-4 py-2 text-right text-neutral-800">{formatNumber(item.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-neutral-50">
                      <tr className="border-t border-neutral-200">
                        <td className="px-4 py-2 font-semibold text-neutral-900">TOTAL QTY</td>
                        <td className="px-4 py-2 font-semibold text-right text-neutral-900">{formatQty(receipt.subtotal_qty)}</td>
                        <td></td>
                        <td></td>
                        <td className="px-4 py-2 font-semibold text-right text-neutral-900">{formatNumber(receipt.subtotal_value)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            ))}

            {!isLoading && detailReceipts.length > 0 && (
              <div className="flex items-center justify-end gap-8 px-4 py-3 font-semibold border-t border-neutral-200 text-neutral-900">
                <span>GRAND TOTAL</span>
                <span>Qty: {formatQty(detailSummary.qty)}</span>
                <span>Nilai: {formatNumber(detailSummary.value)}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
